import React, { useState, useEffect } from 'react';
import { api } from '../../../services/Axios';
import Loader from '../../utils/loader/Loader';
import MessageModal from '../../utils/avisos/MessageModal';
import ConfirmationModal from '../../utils/avisos/ConfirmationModal';
import {
  FaCashRegister,
  FaMoneyBillWave,
  FaCreditCard,
  FaExchangeAlt,
  FaTimesCircle,
  FaCheckCircle,
  FaStore,
  FaUser,
  FaCalendarAlt,
  FaShoppingCart,
  FaCoins,
  FaChartLine,
} from 'react-icons/fa';

interface CashRegister {
  _id: string;
  montoInicial: number;
  montoEsperado: number;
  montoReal?: number;
  diferencia?: number;
  estado: 'abierta' | 'cerrada';
  fechaApertura: string;
  fechaCierre?: string;
  sucursal: {
    _id: string;
    nombre: string;
  };
  cajero: {
    _id: string;
    nombre: string;
    email: string;
  };
  ventas: {
    efectivo: number;
    tarjeta: number;
    transferencia: number;
    total: number;
    cantidad: number;
  };
  notas?: string;
}

interface Sucursal {
  _id: string;
  nombre: string;
}

const CashRegisterManagement: React.FC = () => {
  const [loading, setLoading] = useState(true); // Cambiar a true inicialmente
  const [cajaActiva, setCajaActiva] = useState<CashRegister | null>(null);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [selectedSucursal, setSelectedSucursal] = useState<string>('');
  const [montoInicial, setMontoInicial] = useState<string>('');
  const [nombreCajero, setNombreCajero] = useState<string>('');
  const [montoReal, setMontoReal] = useState<string>('');
  const [notas, setNotas] = useState<string>('');
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info' | 'warning' | 'aviso'>('success');
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

  useEffect(() => {
    loadSucursales();
    loadCajaActiva();
  }, []);

  const loadSucursales = async () => {
    try {
      const response = await api.get('/api/sucursales');
      const sucursalesData = response.data.sucursales || response.data;
      setSucursales(Array.isArray(sucursalesData) ? sucursalesData : []);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
    }
  };

  const loadCajaActiva = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/cash-register/active');
      setCajaActiva(response.data.caja);
      
      // VERIFICAR EL PARÁMETRO AQUÍ, DESPUÉS DE CARGAR LA CAJA
      const params = new URLSearchParams(window.location.search);
      if (params.get('action') === 'close') {
        // Si hay caja abierta Y el parámetro está presente
        setShowCloseModal(true);
        // Limpiar el parámetro
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No hay caja activa
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'close') {
          // Si intentó cerrar pero no hay caja
          setMessageContent('No tienes ninguna caja abierta para cerrar.');
          setMessageType('info');
          setIsMessageModalOpen(true);
          window.history.replaceState({}, '', window.location.pathname);
        }
      } else {
        console.error('Error al cargar caja activa:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCaja = async () => {
    if (!selectedSucursal) {
      setMessageContent('Por favor selecciona una sucursal');
      setMessageType('warning');
      setIsMessageModalOpen(true);
      return;
    }

    if (!nombreCajero.trim()) {
      setMessageContent('Por favor ingresa el nombre del cajero');
      setMessageType('warning');
      setIsMessageModalOpen(true);
      return;
    }

    setLoading(true);
    try {
      const sucursal = sucursales.find(s => s._id === selectedSucursal);
      const response = await api.post('/api/cash-register/open', {
        montoInicial: Number(montoInicial) || 0,
        sucursalId: selectedSucursal,
        sucursalNombre: sucursal?.nombre,
        nombreCajero: nombreCajero.trim(),
      });
      setCajaActiva(response.data.caja);
      setShowOpenModal(false);
      setMontoInicial('');
      setSelectedSucursal('');
      setNombreCajero('');
      
      setShowWelcomeModal(true);
    } catch (error: any) {
      setMessageContent(error.response?.data?.msg || 'Error al abrir la caja');
      setMessageType('error');
      setIsMessageModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCaja = async () => {
    if (!montoReal) {
      setMessageContent('Por favor ingresa el monto real en caja');
      setMessageType('warning');
      setIsMessageModalOpen(true);
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/cash-register/close/${cajaActiva?._id}`, {
        montoReal: Number(montoReal),
        notas,
      });
      setCajaActiva(null);
      setShowCloseModal(false);
      setMontoReal('');
      setNotas('');
      setMessageContent('Caja cerrada exitosamente');
      setMessageType('success');
      setIsMessageModalOpen(true);
    } catch (error: any) {
      setMessageContent(error.response?.data?.msg || 'Error al cerrar la caja');
      setMessageType('error');
      setIsMessageModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) return <Loader />;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <FaCashRegister className="text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Gestión de Caja</h1>
              <p className="text-blue-100 mt-1">
                {cajaActiva ? 'Caja abierta y operativa' : 'Abre una caja para comenzar a operar'}
              </p>
            </div>
          </div>
          {!cajaActiva && (
            <button
              onClick={() => setShowOpenModal(true)}
              className="flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold shadow-md hover:bg-blue-50 transition-all duration-200"
            >
              <FaCashRegister className="mr-2" />
              Abrir Caja
            </button>
          )}
        </div>
      </div>

      {cajaActiva ? (
        <div className="space-y-6">
          {/* Información General de la Caja */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="bg-green-100 p-2 rounded-full">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Caja Abierta</h3>
                </div>
                
                <div className="space-y-2 text-gray-700">
                  <div className="flex items-center space-x-2">
                    <FaStore className="text-blue-600" />
                    <span className="font-medium">Sucursal:</span>
                    <span>{cajaActiva.sucursal.nombre}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaUser className="text-blue-600" />
                    <span className="font-medium">Cajero:</span>
                    <span>{cajaActiva.cajero.nombre}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaCalendarAlt className="text-blue-600" />
                    <span className="font-medium">Apertura:</span>
                    <span className="text-sm">{formatDate(cajaActiva.fechaApertura)}</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setShowCloseModal(true)}
                className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-semibold shadow-md hover:from-red-700 hover:to-red-800 transition-all duration-200 w-full md:w-auto"
              >
                <FaTimesCircle className="mr-2" />
                Cerrar Caja
              </button>
            </div>

            {/* Resumen Financiero Principal */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-semibold mb-1">Monto Inicial</p>
                    <p className="text-2xl font-bold text-purple-700">{formatCurrency(cajaActiva.montoInicial)}</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-full">
                    <FaCoins className="text-purple-600 text-xl" />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm  text-blue-600 font-semibold mb-1">Total Ventas</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(cajaActiva.ventas.total)}</p>
                    <p className="text-xs  text-blue-600 mt-1 flex items-center">
                      <FaShoppingCart className="mr-1" />
                      {cajaActiva.ventas.cantidad} ventas realizadas
                    </p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <FaMoneyBillWave className="text-blue-600 text-xl" />
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-semibold mb-1">Monto Esperado</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(cajaActiva.montoEsperado)}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <FaChartLine className="text-green-600 text-xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ventas por Método de Pago */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <FaCreditCard className="mr-2 text-blue-600" />
              Ventas por Método de Pago
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-medium mb-2">Efectivo</p>
                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(cajaActiva.ventas.efectivo)}</p>
                  </div>
                  <div className="bg-blue-200 p-4 rounded-full">
                    <FaMoneyBillWave className="text-blue-700 text-2xl" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-medium mb-2">Tarjeta</p>
                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(cajaActiva.ventas.tarjeta)}</p>
                  </div>
                  <div className="bg-blue-200 p-4 rounded-full">
                    <FaCreditCard className="text-blue-700 text-2xl" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-medium mb-2">Transferencia</p>
                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(cajaActiva.ventas.transferencia)}</p>
                  </div>
                  <div className="bg-blue-200 p-4 rounded-full">
                    <FaExchangeAlt className="text-blue-700 text-2xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg shadow-md">
          <div className="bg-gray-100 p-6 rounded-full mb-6">
            <FaCashRegister className="text-gray-400 text-6xl" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No hay caja abierta</h3>
          <p className="text-gray-500 mb-6">Abre una caja para comenzar a registrar ventas</p>
          <button
            onClick={() => setShowOpenModal(true)}
            className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
          >
            <FaCashRegister className="mr-2" />
            Abrir Caja
          </button>
        </div>
      )}

      {/* Modal Abrir Caja */}
      <ConfirmationModal
        isOpen={showOpenModal}
        title="Abrir Caja"
        message={
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sucursal *</label>
              <select
                value={selectedSucursal}
                onChange={(e) => setSelectedSucursal(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecciona una sucursal</option>
                {sucursales.map((sucursal) => (
                  <option key={sucursal._id} value={sucursal._id}>
                    {sucursal.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Cajero *
              </label>
              <input
                type="text"
                value={nombreCajero}
                onChange={(e) => setNombreCajero(e.target.value)}
                placeholder="Ej: Juan Pérez"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Ingresa el nombre de la persona que abrirá la caja
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monto Inicial</label>
              <input
                type="number"
                value={montoInicial}
                onChange={(e) => setMontoInicial(e.target.value)}
                placeholder="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ingresa el valor de efectivo en caja al momento de abrirla
              </p>
            </div>
          </div>
        }
        onConfirm={handleOpenCaja}
        onCancel={() => {
          setShowOpenModal(false);
          setMontoInicial('');
          setSelectedSucursal('');
          setNombreCajero('');
        }}
      />

      {/* Modal de Bienvenida después de abrir caja */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fade-in">
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-white p-4 rounded-full">
                  <FaCheckCircle className="text-green-600 text-5xl" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white text-center">
                ¡Caja Abierta Exitosamente!
              </h2>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-lg text-gray-700 mb-2">
                  Todo está listo para comenzar a vender
                </p>
                <p className="text-gray-500">
                  La caja ha sido abierta y está lista para registrar ventas
                </p>
              </div>

              {cajaActiva && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Sucursal:</span>
                    <span className="font-semibold text-gray-800">{cajaActiva.sucursal.nombre}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Cajero:</span>
                    <span className="font-semibold text-gray-800">{cajaActiva.cajero.nombre}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monto inicial:</span>
                    <span className="font-semibold text-green-600">{formatCurrency(cajaActiva.montoInicial)}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowWelcomeModal(false)}
                  className="w-full sm:flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Quedarme Aquí
                </button>
                <a
                  href="/caja-venta"
                  className="w-full sm:flex-1 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center space-x-2"
                >
                  <FaShoppingCart className="text-base flex-shrink-0" />
                  <span className="whitespace-nowrap">Comenzar a Vender</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cerrar Caja */}
      <ConfirmationModal
        isOpen={showCloseModal}
        title="Cerrar Caja"
        message={
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600">Monto Esperado en Caja:</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(cajaActiva?.montoEsperado || 0)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monto Real en Caja *</label>
              <input
                type="number"
                value={montoReal}
                onChange={(e) => setMontoReal(e.target.value)}
                placeholder="Ingresa el monto contado"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {montoReal && (
              <div className={`p-3 rounded-lg ${
                Number(montoReal) - (cajaActiva?.montoEsperado || 0) >= 0
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className="text-sm font-semibold mb-1">
                  Diferencia: {formatCurrency(Math.abs(Number(montoReal) - (cajaActiva?.montoEsperado || 0)))}
                </p>
                <p className={`text-sm ${
                  Number(montoReal) - (cajaActiva?.montoEsperado || 0) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {Number(montoReal) - (cajaActiva?.montoEsperado || 0) >= 0
                    ? '✓ Sobrante en caja'
                    : '✗ Faltante en caja'}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notas (opcional)</label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Observaciones sobre el cierre de caja..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        }
        onConfirm={handleCloseCaja}
        onCancel={() => {
          setShowCloseModal(false);
          setMontoReal('');
          setNotas('');
        }}
      />

      <MessageModal
        isOpen={isMessageModalOpen}
        message={messageContent}
        messageType={messageType}
        onClose={() => setIsMessageModalOpen(false)}
      />
    </div>
  );
};

export default CashRegisterManagement;